import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import { dialog } from '@forest/shared/solid'
import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  type Accessor,
  type JSX,
} from 'solid-js'
import coinX3 from '~/assets/coin-x3.png'
import gemIcon from '~/assets/currency/gem.svg'
import { logButtonClick } from '~/lib/analytics'
import { closeShop } from '~/lib/close-shop'
import { resolveEffectivePrice } from '~/lib/effective-price'
import { intlLocale, supportsNativePaywall } from '~/lib/params'
import { m } from '~/paraglide/messages'
import type { PassivePlusState } from '~/services/bootstrap'
import {
  anyFirstPurchaseBonusActive,
  applyValueBonuses,
  type CoinProduct,
  type CurrencyCatalogue as CurrencyCatalogueData,
  type CurrencyKind,
  type CurrencyProduct,
  type FirstPurchaseBonusOf,
  type TimeCrystalProduct,
} from '~/services/currency-catalogue'
import { CurrencyArtwork, type CurrencyArtworkStatus } from './currency-artwork'
import { StruckOriginalPrice } from './effective-price-parts'
import { FirstPurchaseBonusNotice } from './first-purchase-bonus-notice'
import { FirstPurchaseBonusTagIcon } from './icons/first-purchase-bonus-tag'
import { PlaceholderTextLine } from './placeholder-bar'

// Top-up scrolls (`scrollIntoView({ block: 'start' })`) align a section's top
// with the viewport top, which the fixed header covers. The margin clears
// ShopShell's header on this tab — 8px top padding, 42px balance row, 8px
// gap, 35px tabs, no controls row: 93px — plus the catalogue's 16px of
// breathing room, 109px in all: ShopShell's CATALOGUE_TOP, so the heading
// lands where the first section sits at rest. Keep in step with it.
const sectionScrollMargin = 'scroll-mt-[calc(109px+var(--safe-area-top))]'

// The tab's layout and its rows' footprints, shared by the catalogue, its
// section skeletons, and the boot skeleton's draft (GLOSSARY.md "Boot
// skeleton"): sections 24px apart, each a heading line over rows 8px apart.
const CATALOGUE_CLASS = 'space-y-6 px-3 pb-24'
const SECTION_HEADING_CLASS = 'mb-2 px-2 text-sm font-bold'
const SECTION_ROWS_CLASS = 'space-y-2'
const PRODUCT_ROW_SHAPE_CLASS = 'min-h-[76px] w-full rounded-[24px]'
const PLUS_ROW_SHAPE_CLASS = 'min-h-[82px] rounded-[24px]'
// Rows per section in the boot skeleton: enough to run past a phone screen.
const PLACEHOLDER_ROW_COUNT = 4

const numberFormatter = new Intl.NumberFormat(intlLocale, {
  maximumFractionDigits: 0,
})

// The pack's grant under its art, outlined in white. While a First-purchase
// bonus is active, the original grant is struck through at 12px beside grant
// + bonus.
function GrantLabel(props: {
  firstPurchaseBonus: number | null
  grant: number
}) {
  const total = () => props.grant + (props.firstPurchaseBonus ?? 0)
  // Beside the 12px struck original, a bonus total reads at 14px.
  const totalClass = () =>
    props.firstPurchaseBonus === null ? undefined : 'text-[14px]'
  const original = (layer: 'outline' | 'text') => (
    <StruckOriginalPrice
      class={cn(
        'mr-0.5 text-[12px] font-semibold line-through',
        layer === 'text' && 'opacity-60',
      )}
      original={props.firstPurchaseBonus === null ? null : props.grant}
      testId={
        layer === 'text'
          ? 'currency-grant-original'
          : 'currency-grant-original-outline'
      }
    />
  )
  // Centred on a point 16.5px in from the artwork's right edge; a struck
  // original + bonus total centres 5px further left, per the design.
  return (
    <span
      class={cn(
        'absolute bottom-0 translate-x-1/2 whitespace-nowrap text-xs font-bold leading-[14px] text-[#BD7200]',
        props.firstPurchaseBonus === null ? 'right-[16.5px]' : 'right-[21.5px]',
      )}
    >
      <span
        aria-hidden="true"
        class="absolute inset-0 text-white [-webkit-text-stroke:3px_white]"
      >
        {original('outline')}
        <span class={totalClass()}>{numberFormatter.format(total())}</span>
      </span>
      <span class="relative">
        {original('text')}
        <span class={totalClass()} data-testid="currency-grant">
          {numberFormatter.format(total())}
        </span>
      </span>
    </span>
  )
}

function ProductArtwork<Product extends CurrencyProduct>(props: {
  // The active First-purchase bonus amount, or null.
  firstPurchaseBonus: number | null
  onError: (product: Product) => void
  onLoad: (product: Product) => void
  product: Product
  status: CurrencyArtworkStatus
}) {
  return (
    <CurrencyArtwork
      class="h-14 w-[68px]"
      onError={props.onError}
      onLoad={props.onLoad}
      product={props.product}
      status={props.status}
      testId="currency-artwork"
    >
      {/* The row's ×2 tag takes the +N% badge's place (ProductCard). */}
      <Show when={props.firstPurchaseBonus === null}>
        <Show when={props.product.bonusPercent}>
          {(bonus) => (
            <span class="absolute left-0 top-px flex h-[13px] items-end justify-center rounded-full bg-[#FF6F6F]/[0.72] px-[3px] pb-[1.5px] font-bold leading-none text-white backdrop-blur-[2px] [text-shadow:0_0_2px_rgba(255,255,255,0.41)]">
              <span class="text-[9px]">+{bonus()}</span>
              <span class="text-[6px]">%</span>
            </span>
          )}
        </Show>
      </Show>
      <GrantLabel
        firstPurchaseBonus={props.firstPurchaseBonus}
        grant={props.product.grant}
      />
    </CurrencyArtwork>
  )
}

function Price(props: {
  product: CurrencyProduct
  serverNow: Accessor<number>
}) {
  // Gem packs show the native storefront price and have no sale concept, so
  // these memos resolve to null for them instead of an effective price.
  const amount = createMemo(() => {
    const product = props.product
    return product.currency === 'coin'
      ? resolveEffectivePrice(product.pricing, props.serverNow()).amount
      : null
  })
  const original = createMemo(() => {
    const product = props.product
    return product.currency === 'coin'
      ? resolveEffectivePrice(product.pricing, props.serverNow()).original
      : null
  })
  return (
    <span
      data-testid="currency-price"
      data-amount={amount() ?? undefined}
      data-original={original() ?? undefined}
      class="flex min-h-[30px] shrink-0 items-center gap-0.5 rounded-full bg-white/[0.58] px-2 py-1 text-sm font-semibold leading-[14px] text-[#1FA880]"
    >
      <Show when={props.product.currency === 'coin'}>
        <img src={gemIcon} alt="" class="size-[22px]" />
      </Show>
      <StruckOriginalPrice original={original()} />
      {props.product.currency === 'gem'
        ? props.product.priceLocalized
        : numberFormatter.format(amount()!)}
    </span>
  )
}

function ProductCard<Product extends CurrencyProduct>(props: {
  // Time Crystal cards only: a coin card never carries a First-purchase bonus.
  firstPurchaseBonusOf?: FirstPurchaseBonusOf
  onArtworkError: (product: Product) => void
  onArtworkLoad: (product: Product) => void
  onSelect?: (product: Product) => void
  product: Product
  serverNow: Accessor<number>
  status: CurrencyArtworkStatus
}) {
  // Deduped by value: a clock tick re-renders only when the bonus window
  // closes.
  const firstPurchaseBonus = createMemo(() => {
    const product = props.product
    return product.currency === 'gem'
      ? (props.firstPurchaseBonusOf?.(product) ?? null)
      : null
  })
  // A bonus row is taller: the ×2 tag leads it and the card tucks 30px up
  // under the tag, so the tag rises 12px above the card's top-left corner.
  return (
    <div data-testid="currency-row">
      <Show when={firstPurchaseBonus() !== null}>
        <FirstPurchaseBonusTagIcon
          class="pointer-events-none relative z-10 ml-2.5 block"
          testId="first-purchase-bonus-tag"
        />
      </Show>
      <button
        type="button"
        data-currency={props.product.currency}
        data-product-id={props.product.productId}
        disabled={!props.onSelect}
        onClick={() => props.onSelect?.(props.product)}
        class={cn(
          'flex items-center gap-2 bg-white/45 px-3 py-2 text-left',
          PRODUCT_ROW_SHAPE_CLASS,
          firstPurchaseBonus() !== null && '-mt-[30px]',
        )}
      >
        <ProductArtwork
          firstPurchaseBonus={firstPurchaseBonus()}
          onError={props.onArtworkError}
          onLoad={props.onArtworkLoad}
          product={props.product}
          status={props.status}
        />
        <span class="min-w-0 flex-1 text-sm font-medium leading-5 text-ink">
          {props.product.title}
        </span>
        <Price product={props.product} serverNow={props.serverNow} />
      </button>
    </div>
  )
}

// Placeholder card count per section while hydration runs, taken from the
// bootstrap's collection entry counts (ADR 0023).
export interface CurrencySkeletonCounts {
  coin: number
  gem: number
}

function sectionHeading(currency: CurrencyKind) {
  return currency === 'coin' ? m.coin() : m.time_crystal()
}

function ProductSection<Product extends CurrencyProduct>(props: {
  artworkStatus: (productId: number) => CurrencyArtworkStatus
  currency: CurrencyKind
  // Time Crystal cards only: a coin card never carries a First-purchase bonus.
  firstPurchaseBonusOf?: FirstPurchaseBonusOf
  onArtworkError: (product: Product) => void
  onArtworkLoad: (product: Product) => void
  onSelect?: (product: Product) => void
  products: Product[]
  sectionRef: (element: HTMLElement) => void
  serverNow: Accessor<number>
  // Rendered as given ahead of the cards, under the heading: the coin
  // section's Plus row, the Time Crystals section's First-purchase bonus
  // notice.
  lead?: JSX.Element
}) {
  return (
    <Show when={props.products.length > 0}>
      <section
        ref={(element) => props.sectionRef(element)}
        data-testid={`${props.currency}-section`}
        class={sectionScrollMargin}
      >
        <h2 class={cn(SECTION_HEADING_CLASS, 'text-ink/80')}>
          {sectionHeading(props.currency)}
        </h2>
        <div class={SECTION_ROWS_CLASS}>
          {props.lead}
          <For each={props.products}>
            {(product) => (
              <ProductCard
                firstPurchaseBonusOf={props.firstPurchaseBonusOf}
                onArtworkError={props.onArtworkError}
                onArtworkLoad={props.onArtworkLoad}
                onSelect={props.onSelect}
                product={product}
                serverNow={props.serverNow}
                status={props.artworkStatus(product.productId)}
              />
            )}
          </For>
        </div>
      </section>
    </Show>
  )
}

// Placeholder section while currency hydration runs (ADR 0023): the real
// heading over card-footprint placeholders, one per collection entry, so the
// commit swaps cards in place and section anchors exist for top-up scrolls.
function SectionSkeleton(props: {
  count: number
  currency: CurrencyKind
  // As ProductSection's: rendered ahead of the placeholders.
  lead?: JSX.Element
  sectionRef: (element: HTMLElement) => void
}) {
  return (
    <Show when={props.count > 0}>
      <section
        ref={(element) => props.sectionRef(element)}
        data-testid={`${props.currency}-section-skeleton`}
        class={sectionScrollMargin}
      >
        <h2 class={cn(SECTION_HEADING_CLASS, 'text-ink/80')}>
          {sectionHeading(props.currency)}
        </h2>
        <div class={SECTION_ROWS_CLASS}>
          {props.lead}
          <For each={Array.from({ length: props.count })}>
            {() => (
              <div
                aria-hidden="true"
                class={cn(PRODUCT_ROW_SHAPE_CLASS, 'animate-pulse bg-white/35')}
              />
            )}
          </For>
        </div>
      </section>
    </Show>
  )
}

function PlusRow(props: {
  onPaywallSuccess: () => Promise<void>
  // undefined = trial eligibility still resolving; the CTA slot shows a
  // skeleton instead of guessing between try and get (ADR 0023).
  state: PassivePlusState | undefined
}) {
  const [pending, setPending] = createSignal(false)
  const label = () => {
    if (props.state === 'active') return m.active_state_tag()
    return props.state === 'try' ? m.btn_try_plus() : m.btn_get_plus()
  }
  const openPaywall = async () => {
    if (props.state === undefined || props.state === 'active' || pending()) {
      return
    }
    setPending(true)
    // Past the guard only: a logged click is an actual paywall open attempt.
    logButtonClick(
      props.state === 'try'
        ? 'shop_coin_3x_try_plus'
        : 'shop_coin_3x_subscribe_plus',
    )

    let result
    try {
      result = await native.paywall.openPaywall(
        props.state === 'try' ? 'trial' : 'permanent',
        'shop',
      )
    } catch {
      setPending(false)
      return
    }

    if (!result.success) {
      setPending(false)
      return
    }

    try {
      await props.onPaywallSuccess()
    } catch (error) {
      void dialog.open({
        action: closeShop,
        button: m.btn_got_it(),
        buttonTestId: 'plus-paywall-error-dismiss',
        closeOnEscapeKeyDown: false,
        description: m
          .dialog_something_went_wrong_description()
          .replace(
            '[%s]',
            error instanceof Error ? error.message : String(error),
          ),
        role: 'alertdialog',
        testId: 'plus-paywall-error',
        title: m.dialog_something_went_wrong_title(),
      })
      return
    }
    setPending(false)
  }

  return (
    <div
      data-testid="plus-row"
      data-plus-state={props.state}
      class={cn(
        'flex items-center gap-3 bg-white/45 px-4 py-3',
        PLUS_ROW_SHAPE_CLASS,
      )}
    >
      <img src={coinX3} alt="" class="h-[50px] w-[58px] shrink-0" />
      <span class="min-w-0 flex-1">
        <span class="block text-sm font-semibold leading-5 text-ink">
          {m.triple_coin_item_name()}
        </span>
        <span class="block text-xs leading-4 text-ink/80">
          {m.triple_coin_item_description()}
        </span>
      </span>
      <Show
        when={props.state !== undefined}
        fallback={
          <span
            aria-hidden="true"
            data-testid="plus-cta-skeleton"
            class="h-8 min-w-[84px] shrink-0 animate-pulse rounded-full bg-white/35"
          />
        }
      >
        <Show
          when={props.state === 'active'}
          fallback={
            <button
              type="button"
              data-testid="plus-cta"
              disabled={pending()}
              onClick={() => void openPaywall()}
              class="h-8 min-w-[84px] shrink-0 rounded-full border border-transparent px-3 text-sm font-semibold text-ink [background:linear-gradient(96.49deg,#C5FF70_13.19%,#30D3FF_97.57%)_padding-box,linear-gradient(210deg,#C5FF70,#69FF78,#30D3FF)_border-box] disabled:opacity-50"
            >
              {label()}
            </button>
          }
        >
          <span
            aria-disabled="true"
            class="flex h-[30px] shrink-0 items-center gap-0.5 rounded-full bg-[#DAEEE79C] px-2 text-sm font-semibold text-[#7BABA7]"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.49316 11.0001L9.15987 14.6668L16.5062 7.33337"
                stroke="#7BABA7"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {label()}
          </span>
        </Show>
      </Show>
    </div>
  )
}

export function CurrencyCatalogue(props: {
  artworkStatus: (productId: number) => CurrencyArtworkStatus
  // undefined while currency hydration runs in the background (ADR 0023).
  catalogue: CurrencyCatalogueData | undefined
  firstPurchaseBonusOf: FirstPurchaseBonusOf
  onArtworkError: (product: CurrencyProduct) => void
  onArtworkLoad: (product: CurrencyProduct) => void
  onPlusPaywallSuccess: () => Promise<void>
  onSelectCoinExchange: (product: CoinProduct) => void
  onSelectTimeCrystalPurchase: (product: TimeCrystalProduct) => void
  // Terminal trial-eligibility failure hides the row entirely: a wrong CTA
  // is never shown, at the cost of the upsell exposure (ADR 0023).
  plusRowHidden: boolean
  plusState: PassivePlusState | undefined
  serverNow: Accessor<number>
  skeletonCounts: CurrencySkeletonCounts
  target?: CurrencyKind
  // The Time Crystals section leads the coins (GLOSSARY.md "First-purchase
  // bonus (首購翻倍)"); otherwise it follows them.
  timeCrystalsLead: boolean
}) {
  let coinSection: HTMLElement | undefined
  let gemSection: HTMLElement | undefined
  let catalogueRoot: HTMLElement | undefined
  const hydrated = () => props.catalogue !== undefined

  // Scrolls again as the catalogue hydrates: the sections' order is decided
  // then, and the target may have moved.
  createEffect(
    on([() => props.target, hydrated], ([target]) => {
      const section =
        (target === 'coin' ? coinSection : gemSection) ?? catalogueRoot
      if (target) section?.scrollIntoView({ block: 'start' })
    }),
  )

  // The +N% badges follow effective prices (ADR 0027). The amounts memo
  // dedupes clock ticks by value equality: the badge list recomputes only
  // when a coin pack's effective price actually flips across a sale-window
  // edge.
  const coinEffectiveAmounts = createMemo(
    () =>
      props.catalogue?.coin.map(
        (product) =>
          resolveEffectivePrice(product.pricing, props.serverNow()).amount,
      ) ?? [],
    [] as number[],
    {
      equals: (previous, next) =>
        previous.length === next.length &&
        previous.every((amount, index) => amount === next[index]),
    },
  )
  const coinProducts = createMemo(() => {
    const catalogue = props.catalogue
    if (!catalogue) return undefined
    return applyValueBonuses(catalogue.coin, coinEffectiveAmounts())
  })
  const gemProducts = createMemo(() => {
    const catalogue = props.catalogue
    if (!catalogue) return undefined
    return applyValueBonuses(
      catalogue.gem,
      catalogue.gem.map((product) => product.priceValue),
    )
  })
  // The cards' own reading, so the notice hides with the last active
  // First-purchase bonus — closed or spent.
  const noticeShowing = createMemo(() => {
    const catalogue = props.catalogue
    return catalogue
      ? anyFirstPurchaseBonusActive(catalogue.gem, props.firstPurchaseBonusOf)
      : false
  })

  // Created once, so the one row moves from the skeleton into the hydrated
  // section with its state (GLOSSARY.md "Plus row"). Without coin cards or
  // placeholders it stands alone in the section's place.
  const plusRow = (
    <Show when={supportsNativePaywall && !props.plusRowHidden}>
      <PlusRow
        onPaywallSuccess={props.onPlusPaywallSuccess}
        state={props.plusState}
      />
    </Show>
  )
  const coins = () => (
    <Show
      when={props.catalogue}
      fallback={
        <Show when={props.skeletonCounts.coin > 0} fallback={plusRow}>
          <SectionSkeleton
            count={props.skeletonCounts.coin}
            currency="coin"
            lead={plusRow}
            sectionRef={(element) => {
              coinSection = element
            }}
          />
        </Show>
      }
    >
      <Show when={coinProducts()!.length > 0} fallback={plusRow}>
        <ProductSection
          artworkStatus={props.artworkStatus}
          currency="coin"
          lead={plusRow}
          onArtworkError={props.onArtworkError}
          onArtworkLoad={props.onArtworkLoad}
          onSelect={props.onSelectCoinExchange}
          products={coinProducts()!}
          sectionRef={(element) => {
            coinSection = element
          }}
          serverNow={props.serverNow}
        />
      </Show>
    </Show>
  )

  const timeCrystals = () => (
    <Show
      when={props.catalogue}
      fallback={
        <SectionSkeleton
          count={props.skeletonCounts.gem}
          currency="gem"
          sectionRef={(element) => {
            gemSection = element
          }}
        />
      }
    >
      <ProductSection
        artworkStatus={props.artworkStatus}
        currency="gem"
        firstPurchaseBonusOf={props.firstPurchaseBonusOf}
        lead={
          <Show when={noticeShowing()}>
            <FirstPurchaseBonusNotice />
          </Show>
        }
        onArtworkError={props.onArtworkError}
        onArtworkLoad={props.onArtworkLoad}
        onSelect={props.onSelectTimeCrystalPurchase}
        products={gemProducts()!}
        sectionRef={(element) => {
          gemSection = element
        }}
        serverNow={props.serverNow}
      />
    </Show>
  )

  return (
    <div
      ref={(element) => {
        catalogueRoot = element
      }}
      data-testid="currency-catalogue"
      data-target={props.target}
      data-hydrated={hydrated() ? 'true' : 'false'}
      class={CATALOGUE_CLASS}
    >
      <Show
        when={props.timeCrystalsLead}
        fallback={
          <>
            {coins()}
            {timeCrystals()}
          </>
        }
      >
        {timeCrystals()}
        {coins()}
      </Show>
    </div>
  )
}

/**
 * The Coins & Crystals tab as the boot skeleton draws it — a first draft,
 * pending design sign-off: the coin section's heading over the Plus row and
 * its packs, then the Time Crystals section's, on the catalogue's own
 * footprints. Data-independent, so it cannot know whether the Time
 * Crystals lead (ADR 0047) or how many packs there are.
 */
export function CurrencyCataloguePlaceholder() {
  const heading = (width: string) => (
    <PlaceholderTextLine
      class={cn('h-3', width)}
      typeClass={SECTION_HEADING_CLASS}
    />
  )
  const rows = () => (
    <For each={Array.from({ length: PLACEHOLDER_ROW_COUNT })}>
      {() => <div class={cn(PRODUCT_ROW_SHAPE_CLASS, 'bg-white/35')} />}
    </For>
  )
  return (
    <div class={CATALOGUE_CLASS}>
      <section>
        {heading('w-12')}
        <div class={SECTION_ROWS_CLASS}>
          <Show when={supportsNativePaywall}>
            <div class={cn(PLUS_ROW_SHAPE_CLASS, 'bg-white/35')} />
          </Show>
          {rows()}
        </div>
      </section>
      <section>
        {heading('w-24')}
        <div class={SECTION_ROWS_CLASS}>{rows()}</div>
      </section>
    </div>
  )
}
